import {Exclude, Expose, instanceToPlain, plainToInstance, Transform} from "class-transformer";
import * as t from "io-ts";
import {validateJSON, validateJSONNow} from "../../web/validation";
import {Entity} from "../entity";
import {RrcWasteType, toRrcWasteType} from "./rrc-waste-type";
import {getDatabaseCodeTransformer} from "../transfomers/transformers";
import {RrcUnit, toRrcUnit} from "./rrc-unit";
import {RrcDiscrepancyType, toRrcDiscrepancyType} from "./rrc-discrepancy-type";

export const RrcEp10WasteManifestCodec = t.intersection([
  t.type({
    id: t.string,
    version: t.number,
    status: t.string,
    ttaId: t.string,
    manifestDttm: t.number,
    manifestNumber: t.string,
    generatorRrcCompanyProfileId: t.string,
    generatorRrcCompanyProfileEffectiveDttm: t.number,
    generatorOrgName: t.string,
    generatorP5Number: t.string,
    generatorCommercial: t.boolean,
    generatorRrcLocationProfileId: t.string,
    generatorRrcLocationProfileEffectiveDttm: t.number,
    generatorLocationId: t.string,
    generatorLocationType: t.string,
    generatorLocationFacilityName: t.string,
    transporterRrcCompanyProfileId: t.string,
    transporterRrcCompanyProfileEffectiveDttm: t.number,
    transporterOrgName: t.string,
    transporterPermitNumber: t.string,
    generatorSignedById: t.string,
    generatorSignedByName: t.string,
    generatorSignedDttm: t.number,
    transporterSignedById: t.string,
    transporterSignedByName: t.string,
    transporterSignedDttm: t.number,
    snapshotJson: t.string,
    snapshotHash: t.string,
    createDttm: t.number,
    lastModifiedDttm: t.number,
  }),
  t.partial({
    supersedesId: t.string,
    supersededById: t.string,
    generatorContactName: t.string,
    generatorContactPhone: t.string,
    generatorContactEmail: t.string,
    generatorLocationLeaseName: t.string,
    generatorLocationLeaseNumber: t.string,
    generatorLocationGasPlantNumber: t.string,
    generatorLocationApiNumber: t.string,
    generatorLocationOtherIdentifier: t.string,
    generatorLocationLatCoord: t.number,
    generatorLocationLongCoord: t.number,
    generatorLocationCounty: t.string,
    waste1RrcEp9WasteProfileId: t.string,
    waste1RrcEp9WasteProfileEffectiveDttm: t.number,
    waste1Id: t.string,
    waste1Type: t.string,
    waste1RcraExempt: t.boolean,
    waste1Quantity: t.number,
    waste1QuantityUnit: t.string,
    waste2RrcEp9WasteProfileId: t.string,
    waste2RrcEp9WasteProfileEffectiveDttm: t.number,
    waste2Id: t.string,
    waste2Type: t.string,
    waste2RcraExempt: t.boolean,
    waste2Quantity: t.number,
    waste2QuantityUnit: t.string,
    waste3RrcEp9WasteProfileId: t.string,
    waste3RrcEp9WasteProfileEffectiveDttm: t.number,
    waste3Id: t.string,
    waste3Type: t.string,
    waste3RcraExempt: t.boolean,
    waste3Quantity: t.number,
    waste3QuantityUnit: t.string,
    waste4RrcEp9WasteProfileId: t.string,
    waste4RrcEp9WasteProfileEffectiveDttm: t.number,
    waste4Id: t.string,
    waste4Type: t.string,
    waste4RcraExempt: t.boolean,
    waste4Quantity: t.number,
    waste4QuantityUnit: t.string,
    waste5RrcEp9WasteProfileId: t.string,
    waste5RrcEp9WasteProfileEffectiveDttm: t.number,
    waste5Id: t.string,
    waste5Type: t.string,
    waste5RcraExempt: t.boolean,
    waste5Quantity: t.number,
    waste5QuantityUnit: t.string,
    wasteDescription: t.string,
    wasteNormReading: t.number,
    outOfStateState: t.string,
    transporterP5Number: t.string,
    transporterContactName: t.string,
    transporterContactPhone: t.string,
    transporterContactEmail: t.string,
    transporterDiscrepancyType: t.string,
    transporterDiscrepancyDetails: t.string,
    receiverRrcCompanyProfileId: t.string,
    receiverRrcCompanyProfileEffectiveDttm: t.number,
    receiverOrgName: t.string,
    receiverContactName: t.string,
    receiverContactPhone: t.string,
    receiverContactEmail: t.string,
    receiverRrcLocationProfileId: t.string,
    receiverRrcLocationProfileEffectiveDttm: t.number,
    receiverLocationId: t.string,
    receiverLocationType: t.string,
    receiverLocationFacilityName: t.string,
    receiverLocationFacilityPermitNumber: t.string,
    receiverAcceptanceDttm: t.number,
    receiverAcceptancePaintFilterPass: t.boolean,
    receiverAcceptanceNormReading: t.number,
    receiverDiscrepancyType: t.string,
    receiverDiscrepancyDetails: t.string,
    receiverSignedById: t.string,
    receiverSignedByName: t.string,
    receiverSignedDttm: t.number,
    regionId: t.string,
    createdById: t.string,
    lastModifiedById: t.string,
    createdByName: t.string,
    lastModifiedByName: t.string,
    tenantCode: t.string,
  }),
]);

export type RrcEp10WasteManifest = t.TypeOf<typeof RrcEp10WasteManifestCodec>;

@Exclude()
export class RrcEp10WasteManifestEntity implements Entity {
  @Expose()
  id!: string;

  @Expose()
  version!: number;

  @Expose()
  status!: string;

  @Expose()
  supersedesId?: string;

  @Expose()
  supersededById?: string;

  @Expose()
  ttaId!: string;

  @Expose()
  manifestDttm!: number

  @Expose()
  manifestNumber!: string;

  @Expose()
  generatorRrcCompanyProfileId!: string;

  @Expose()
  generatorRrcCompanyProfileEffectiveDttm!: number;

  @Expose()
  generatorOrgName!: string;

  @Expose()
  generatorP5Number!: string;

  @Expose()
  generatorCommercial = false;

  @Expose()
  generatorContactName?: string;

  @Expose()
  generatorContactPhone?: string;

  @Expose()
  generatorContactEmail?: string;

  @Expose()
  generatorRrcLocationProfileId!: string;

  @Expose()
  generatorRrcLocationProfileEffectiveDttm!: number;

  @Expose()
  generatorLocationId!: string;

  @Expose()
  generatorLocationType!: string;

  @Expose()
  generatorLocationFacilityName!: string;

  @Expose()
  generatorLocationLeaseName?: string;

  @Expose()
  generatorLocationLeaseNumber?: string;

  @Expose()
  generatorLocationGasPlantNumber?: string;

  @Expose()
  generatorLocationApiNumber?: string;

  @Expose()
  generatorLocationOtherIdentifier?: string;

  @Expose()
  generatorLocationLatCoord?: number;

  @Expose()
  generatorLocationLongCoord?: number;

  @Expose()
  generatorLocationCounty?: string;

  @Expose()
  waste1RrcEp9WasteProfileId?: string;

  @Expose()
  waste1RrcEp9WasteProfileEffectiveDttm?: number;

  @Expose()
  waste1Id?: string;

  @Transform(getDatabaseCodeTransformer(toRrcWasteType))
  @Expose()
  waste1Type?: RrcWasteType;

  @Expose()
  waste1RcraExempt?: boolean;

  @Expose()
  waste1Quantity?: number;

  @Transform(getDatabaseCodeTransformer(toRrcUnit))
  @Expose()
  waste1QuantityUnit?: RrcUnit;

  @Expose()
  waste2RrcEp9WasteProfileId?: string;

  @Expose()
  waste2RrcEp9WasteProfileEffectiveDttm?: number;

  @Expose()
  waste2Id?: string;

  @Transform(getDatabaseCodeTransformer(toRrcWasteType))
  @Expose()
  waste2Type?: RrcWasteType;

  @Expose()
  waste2RcraExempt?: boolean;

  @Expose()
  waste2Quantity?: number;

  @Transform(getDatabaseCodeTransformer(toRrcUnit))
  @Expose()
  waste2QuantityUnit?: RrcUnit;

  @Expose()
  waste3RrcEp9WasteProfileId?: string;

  @Expose()
  waste3RrcEp9WasteProfileEffectiveDttm?: number;

  @Expose()
  waste3Id?: string;

  @Transform(getDatabaseCodeTransformer(toRrcWasteType))
  @Expose()
  waste3Type?: RrcWasteType;

  @Expose()
  waste3RcraExempt?: boolean;

  @Expose()
  waste3Quantity?: number;

  @Transform(getDatabaseCodeTransformer(toRrcUnit))
  @Expose()
  waste3QuantityUnit?: RrcUnit;

  @Expose()
  waste4RrcEp9WasteProfileId?: string;

  @Expose()
  waste4RrcEp9WasteProfileEffectiveDttm?: number;

  @Expose()
  waste4Id?: string;

  @Transform(getDatabaseCodeTransformer(toRrcWasteType))
  @Expose()
  waste4Type?: RrcWasteType;

  @Expose()
  waste4RcraExempt?: boolean;

  @Expose()
  waste4Quantity?: number;

  @Transform(getDatabaseCodeTransformer(toRrcUnit))
  @Expose()
  waste4QuantityUnit?: RrcUnit;

  @Expose()
  waste5RrcEp9WasteProfileId?: string;

  @Expose()
  waste5RrcEp9WasteProfileEffectiveDttm?: number;

  @Expose()
  waste5Id?: string;

  @Transform(getDatabaseCodeTransformer(toRrcWasteType))
  @Expose()
  waste5Type?: RrcWasteType;

  @Expose()
  waste5RcraExempt?: boolean;

  @Expose()
  waste5Quantity?: number;

  @Transform(getDatabaseCodeTransformer(toRrcUnit))
  @Expose()
  waste5QuantityUnit?: RrcUnit;

  @Expose()
  wasteDescription?: string;

  @Expose()
  wasteNormReading?: number;

  @Expose()
  outOfStateState?: string;

  @Expose()
  transporterRrcCompanyProfileId!: string;

  @Expose()
  transporterRrcCompanyProfileEffectiveDttm!: number;

  @Expose()
  transporterOrgName!: string;

  @Expose()
  transporterPermitNumber!: string;

  @Expose()
  transporterP5Number?: string;

  @Expose()
  transporterContactName?: string;

  @Expose()
  transporterContactPhone?: string;

  @Expose()
  transporterContactEmail?: string;

  @Transform(getDatabaseCodeTransformer(toRrcDiscrepancyType))
  @Expose()
  transporterDiscrepancyType?: RrcDiscrepancyType;

  @Expose()
  transporterDiscrepancyDetails?: string;

  @Expose()
  receiverRrcCompanyProfileId?: string;

  @Expose()
  receiverRrcCompanyProfileEffectiveDttm?: number;

  @Expose()
  receiverOrgName?: string;

  @Expose()
  receiverContactName?: string;

  @Expose()
  receiverContactPhone?: string;

  @Expose()
  receiverContactEmail?: string;

  @Expose()
  receiverRrcLocationProfileId?: string;

  @Expose()
  receiverRrcLocationProfileEffectiveDttm?: number;

  @Expose()
  receiverLocationId?: string;

  @Expose()
  receiverLocationType?: string;

  @Expose()
  receiverLocationFacilityName?: string;

  @Expose()
  receiverLocationFacilityPermitNumber?: string;

  @Transform(getDatabaseCodeTransformer(toRrcDiscrepancyType))
  @Expose()
  receiverDiscrepancyType?: RrcDiscrepancyType;

  @Expose()
  receiverAcceptanceDttm?: number;

  @Expose()
  receiverAcceptancePaintFilterPass?: boolean;

  @Expose()
  receiverAcceptanceNormReading?: number;

  @Expose()
  receiverDiscrepancyDetails?: string;

  @Expose()
  generatorSignedById!: string;

  @Expose()
  generatorSignedByName!: string;

  @Expose()
  generatorSignedDttm!: number;

  @Expose()
  transporterSignedById!: string;

  @Expose()
  transporterSignedByName!: string;

  @Expose()
  transporterSignedDttm!: number;

  @Expose()
  receiverSignedById?: string;

  @Expose()
  receiverSignedByName?: string;

  @Expose()
  receiverSignedDttm?: number;

  @Expose()
  snapshotJson!: string;

  @Expose()
  snapshotHash!: string;

  @Expose()
  createdById?: string;

  @Expose()
  lastModifiedById?: string;

  @Expose()
  createDttm!: number;

  @Expose()
  lastModifiedDttm!: number;

  @Expose()
  regionId?: string;

  @Expose()
  createdByName?: string;

  @Expose()
  lastModifiedByName?: string;

  @Expose()
  tenantCode?: string;

  readonly entityType = 'RRCEP10WASTEMFST';

  get displayName(): Readonly<string> {
    return this.id;
  }

  /**
   * Constructor for RrcEp10WasteManifestEntity. Recommend using the {@link create} method instead.
   * @hideconstructor
   */
  constructor() {
    // Empty constructor for 'class-transformer'
  }

  static from(value: RrcEp10WasteManifest): RrcEp10WasteManifestEntity {
    return plainToInstance(RrcEp10WasteManifestEntity, value, {
      excludeExtraneousValues: true,
    });
  }

  loadTypeValue(): Promise<RrcEp10WasteManifest> {
    const object = instanceToPlain(this, {
      excludeExtraneousValues: true,
    });
    return validateJSON(RrcEp10WasteManifestCodec, object);
  }

  asTypeValue(): RrcEp10WasteManifest {
    const object = instanceToPlain(this, {
      excludeExtraneousValues: true,
    });
    return validateJSONNow(RrcEp10WasteManifestCodec, object);
  }
}

// Pickup manifests need not have a ticket, signatures, or a receiver yet.
export type RrcEp10Document = Pick<
  RrcEp10WasteManifest,
  'id' | 'manifestNumber' | 'version' | 'status'
> &
  Partial<
    Omit<RrcEp10WasteManifest, 'id' | 'manifestNumber' | 'version' | 'status'>
  >;
export type ComplianceExportFormat = 'pdf' | 'csv';
